import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { SubcontractorBidsBreadcrumbs } from '@/features/subcontractor-bids/components/subcontractor-bids-breadcrumbs';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Header, HeaderBackButton } from '@/ui/components/header';
import { H1 } from '@/ui/components/heading';
import { Main, MainContainer, MainHeader, MainScrollArea, MainPending } from '@/ui/components/main';
import {
  PlusSignIcon,
  StarIcon as StarIconOutline,
  FolderIcon,
  LinkSquare02Icon,
  Delete02Icon,
  ArrowRight02Icon,
  PencilEdit02Icon,
  DashboardSquare01Icon,
  Menu07Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { ActionToolbar, ActionToolbarButton } from '@/ui/components/action-toolbar';
import { Button } from '@/ui/components/button';
import { Card } from '@/ui/components/card';
import { Menu, MenuItem, MenuPopup } from '@/ui/components/menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/ui/components/alert-dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { WorkPackagesIcon } from '@/ui/components/empty-icons';
import { noop, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { Fragment, useState } from 'react';
import {
  createProcurementOpenAtom,
  procurementsViewAtom,
} from '@/features/subcontractor-bids/store';
import { CreateProcurement } from '@/features/subcontractor-bids/components/create-procurement';
import { DocumentsTree } from '@/features/subcontractor-bids/components/documents-tree';
import { procurementsQuery } from '@/features/subcontractor-bids/queries';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import {
  canCreateWorkPackageAgainstKg,
  packCreationBlocker,
} from '@/features/subcontractor-bids/utils';
import { ScopeSheetAlert } from '@/features/subcontractor-bids/components/scope-sheet-alert';
import { scopeAgentStatusQuery } from '@/features/scope-breakdown/queries';
import {
  useDeleteProcurement,
  useUpdateProcurement,
} from '@/features/subcontractor-bids/mutations';
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogFooter,
  DialogPopup,
  DialogScrollArea,
  DialogTitle,
  DialogTitleBreadcrumb,
  DialogXClose,
} from '@/ui/components/dialog';
import { Field, FieldControl, FieldError, FieldLabel } from '@/ui/components/form/field';
import { Form } from '@/ui/components/form';
import { Badge } from '@/ui/components/badge';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { entityDocumentsQuery } from '@/features/documents/queries';
import { useKgStatus } from '@/features/documents/helpers';
import { useFavorites } from '@/features/favorites/helpers';
import { ProcurementPackage } from '@/features/subcontractor-bids/types';
import { AuctionIcon, StarIcon, ToolsIcon } from '@hugeicons-pro/core-duotone-rounded';
import { Group, GroupItem, GroupSeparator } from '@/ui/components/group';
import { PROCUREMENTS_VIEWS } from '@/features/subcontractor-bids/constants';
import { Toggle } from '@/ui/components/toggle';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Table, TableBody, TableRow, TableRowMenuTrigger } from '@/ui/components/table';
import { DataTableHeader, DataTableCell } from '@/ui/components/data-table';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/subcontractor-bids/',
)({
  component: RouteComponent,
  pendingComponent: MainPending,
  loader: (opts) => {
    opts.context.queryClient
      .query(entityDocumentsQuery(opts.params.workspaceId, 'tenderPackage'))
      .catch(noop);
    return opts.context.queryClient.query({
      ...procurementsQuery(opts.params.workspaceId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const { projectId, workspaceId } = Route.useParams();
  const { data: procurements } = useSuspenseQuery(procurementsQuery(workspaceId));
  const [view, setView] = useAtom(procurementsViewAtom);
  const { data: kgStatus } = useKgStatus(workspaceId, 'tenderPackage');

  return (
    <Main>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId"
          params={{ projectId, workspaceId }}
        />
        <SubcontractorBidsBreadcrumbs />
      </Header>
      <MainScrollArea>
        {!kgStatus?.wpReady && (
          <ScopeSheetAlert workspaceId={workspaceId} documentType="tenderPackage" />
        )}
        <IndexingStatusAlerts resourceId={workspaceId} documentType="tenderPackage" waitForKg />
        {procurements.length === 0 ? (
          <Empty>
            <EmptyMedia>
              <WorkPackagesIcon />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{t({ en: 'Procurement packages', nb: 'Anskaffelsespakker' })}</EmptyTitle>
              <EmptyDescription>
                {t({
                  en: 'Create a procurement package to collect and compare subcontractor bids.',
                  nb: 'Opprett en anskaffelsespakke for å samle inn og sammenligne underleverandørtilbud.',
                })}
              </EmptyDescription>
              <NewPackButton />
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <MainHeader>
              <H1>{t({ en: 'Subcontractor procurement', nb: 'Underleverandøranskaffelse' })}</H1>
              <div className="flex items-center max-md:w-full gap-1.5">
                <Group className="max-md:w-full">
                  {PROCUREMENTS_VIEWS.map((item, idx) => (
                    <Fragment key={item}>
                      <GroupItem
                        render={
                          <Toggle
                            pressed={view === item}
                            onPressedChange={() => setView(item)}
                            render={
                              <Button
                                variant="secondary"
                                className="max-md:w-full"
                                kind="with-icon"
                              >
                                <Icon icon={item === 'list' ? Menu07Icon : DashboardSquare01Icon} />
                                {t(common[item])}
                              </Button>
                            }
                          />
                        }
                      />
                      {idx < PROCUREMENTS_VIEWS.length - 1 && <GroupSeparator />}
                    </Fragment>
                  ))}
                </Group>
                <NewPackButton />
              </div>
            </MainHeader>
            <MainContainer>
              <div className="flex gap-4 @5xl:gap-8 @5xl:flex-row flex-col items-start">
                {view === 'list' ? (
                  <ProcurementsTable procurements={procurements} />
                ) : (
                  <div className="grid @2xl:grid-cols-2 @7xl:grid-cols-3 gap-4 w-full">
                    {procurements.map((item) => {
                      const openLabel = t({
                        en: `Open ${item.displayName}`,
                        nb: `Åpne ${item.displayName}`,
                      }).toLowerCase();

                      return (
                        <Card
                          render={
                            <Link
                              className="cursor-pointer relative group hover:shadow-md/4 transition-shadow flex flex-col p-3"
                              to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId"
                              params={{ projectId, workspaceId, procurementId: item.id }}
                            />
                          }
                          key={item.id}
                        >
                          <div className="flex -mt-0.5">
                            <p className="font-medium line-clamp-1">{item.displayName}</p>
                            <ProcurementFavoriteToggle item={item} />
                            <ProcurementActions item={item} />
                          </div>
                          <div className="flex mt-2">
                            <div>
                              <p className="text-muted flex items-center gap-1.5 text-sm">
                                <span className="flex items-center gap-3 flex-wrap md:group-hover:hidden">
                                  <span className="flex items-center gap-1.5">
                                    <Icon icon={ToolsIcon} className="size-4 text-muted" />
                                    {t({
                                      en:
                                        item.disciplines.length === 1
                                          ? `${item.disciplines.length} discipline covered`
                                          : `${item.disciplines.length} disciplines covered`,
                                      nb:
                                        item.disciplines.length === 1
                                          ? `${item.disciplines.length} disiplin dekket`
                                          : `${item.disciplines.length} disipliner dekket`,
                                    })}
                                  </span>
                                  <span className="flex items-center gap-1.5">
                                    <Icon icon={AuctionIcon} className="size-4 text-muted" />
                                    {t({
                                      en:
                                        item.numberOfBids === 1
                                          ? `${item.numberOfBids} supplier bid`
                                          : `${item.numberOfBids} supplier bids`,
                                      nb:
                                        item.numberOfBids === 1
                                          ? `${item.numberOfBids} leverandørtilbud`
                                          : `${item.numberOfBids} leverandørtilbud`,
                                    })}
                                  </span>
                                </span>
                                <span className="hidden md:group-hover:inline">
                                  {openLabel.charAt(0).toUpperCase() + openLabel.slice(1)}
                                </span>
                              </p>
                            </div>
                            <Icon
                              icon={ArrowRight02Icon}
                              className="md:invisible ml-auto group-hover:visible size-5 -mr-0.5"
                            />
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
                <Card className="overflow-hidden divide-y @5xl:max-w-96 w-full">
                  <div className="py-2 px-3">
                    <div className="flex items-center mb-1 gap-2">
                      <p className="font-medium text-sm">
                        {t({ en: 'Shared context', nb: 'Delt kontekst' })}
                      </p>
                      <SuspenseBoundary>
                        <SharedContextFileCount />
                      </SuspenseBoundary>
                    </div>
                    <p className="text-muted text-xs">
                      {t({
                        en: 'Files and context used for subcontractor bids is shown below.',
                        nb: 'Filer og kontekst brukt for underleverandørbid er vist nedenfor.',
                      })}
                    </p>
                  </div>
                  <div className="px-3 pb-3 pt-2">
                    <SuspenseBoundary>
                      <RfpFilesHeading />
                    </SuspenseBoundary>
                    <DocumentsTree resourceId={workspaceId} documentType="tenderPackage" />
                    <SuspenseBoundary>
                      <RfpFilesLink />
                    </SuspenseBoundary>
                  </div>
                </Card>
              </div>
            </MainContainer>
          </>
        )}
        <CreateProcurement />
      </MainScrollArea>
    </Main>
  );
}

function ProcurementsTable({ procurements }: { procurements: ProcurementPackage[] }) {
  const { projectId, workspaceId } = Route.useParams();
  const navigate = useNavigate();

  const columns = [
    {
      accessorKey: 'displayName',
      size: 200,
      meta: { primary: true },
      header: t({ en: 'Pack', nb: 'Pakke' }),
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 font-medium">{row.original.displayName}</div>
      ),
    },
    {
      id: 'disciplines',
      header: t({ en: 'Disciplines', nb: 'Fagområder' }),
      size: 200,
      cell: ({ row }) => (
        <span className="text-muted flex items-center gap-1.5">
          <Icon icon={ToolsIcon} className="size-4 text-muted" />
          {t({
            en:
              row.original.disciplines.length === 1
                ? `${row.original.disciplines.length} discipline covered`
                : `${row.original.disciplines.length} disciplines covered`,
            nb:
              row.original.disciplines.length === 1
                ? `${row.original.disciplines.length} disiplin dekket`
                : `${row.original.disciplines.length} disipliner dekket`,
          })}
        </span>
      ),
    },
    {
      id: 'bids',
      header: t({ en: 'Supplier bids', nb: 'Leverandørtilbud' }),
      size: 180,
      cell: ({ row }) => (
        <span className="text-muted flex items-center gap-1.5">
          <Icon icon={AuctionIcon} className="size-4 text-muted" />
          {t({
            en:
              row.original.numberOfBids === 1
                ? `${row.original.numberOfBids} supplier bid`
                : `${row.original.numberOfBids} supplier bids`,
            nb:
              row.original.numberOfBids === 1
                ? `${row.original.numberOfBids} leverandørtilbud`
                : `${row.original.numberOfBids} leverandørtilbud`,
          })}
        </span>
      ),
    },
    {
      id: 'actions',
      size: 90,
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end">
          <ProcurementActions item={row.original} />
        </div>
      ),
    },
  ] satisfies ColumnDef<ProcurementPackage>[];

  const table = useReactTable({
    columns,
    data: procurements,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card className="overflow-hidden w-full">
      <Table>
        <DataTableHeader table={table} />
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              onClick={() =>
                navigate({
                  to: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId',
                  params: { projectId, workspaceId, procurementId: row.original.id },
                })
              }
            >
              {row.getVisibleCells().map((cell) => (
                <DataTableCell key={cell.id} cell={cell} />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ProcurementFavoriteToggle({ item }: { item: ProcurementPackage }) {
  const { projectId, workspaceId } = Route.useParams();
  const { isFavorited, toggleFavorite } = useFavorites();
  const favorited = isFavorited(item.id);

  return (
    <ActionToolbar visible className="ml-1.5 -mt-1">
      <ActionToolbarButton
        label={favorited ? t(common.unfavorite) : t(common.favorite)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleFavorite({
            id: item.id,
            name: item.displayName,
            subject: 'procurement',
            projectId,
            evaluationId: workspaceId,
          });
        }}
      >
        <Icon icon={favorited ? StarIcon : StarIconOutline} />
      </ActionToolbarButton>
    </ActionToolbar>
  );
}

function ProcurementActions({ item }: { item: ProcurementPackage }) {
  const { projectId, workspaceId } = Route.useParams();
  const { isFavorited, toggleFavorite } = useFavorites();
  const favorited = isFavorited(item.id);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [displayName, setDisplayName] = useState(item.displayName);

  const renameProcurement = useUpdateProcurement(item.id);
  const deleteProcurement = useDeleteProcurement(item.id);

  return (
    <>
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className="ml-auto -mr-1 not-in-[table]:-mt-0.5"
      >
        <Menu>
          <TableRowMenuTrigger />
          <MenuPopup align="end">
            <MenuItem
              onClick={() =>
                toggleFavorite({
                  id: item.id,
                  name: item.displayName,
                  subject: 'procurement',
                  projectId,
                  evaluationId: workspaceId,
                })
              }
            >
              <Icon icon={favorited ? StarIcon : StarIconOutline} />
              {favorited ? t(common.unfavorite) : t(common.favorite)}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setDisplayName(item.displayName);
                setIsRenameOpen(true);
              }}
            >
              <Icon icon={PencilEdit02Icon} />
              {t({ en: 'Rename', nb: 'Gi nytt navn' })}
            </MenuItem>
            <MenuItem variant="destructive" onClick={() => setIsDeleteOpen(true)}>
              <Icon icon={Delete02Icon} />
              {t({ en: 'Delete', nb: 'Slett' })}
            </MenuItem>
          </MenuPopup>
        </Menu>
      </div>

      <div className="contents" onClick={(e) => e.stopPropagation()}>
        <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
          <DialogPopup>
            <DialogTitle>
              <DialogTitleBreadcrumb>
                {t({ en: 'Rename pack', nb: 'Gi pakke nytt navn' })}
              </DialogTitleBreadcrumb>
              <DialogXClose />
            </DialogTitle>
            <DialogContent
              render={
                <Form
                  onFormSubmit={() =>
                    renameProcurement.mutate(
                      { displayName },
                      { onSuccess: () => setIsRenameOpen(false) },
                    )
                  }
                />
              }
            >
              <DialogScrollArea>
                <Field>
                  <FieldLabel>{t({ en: 'Name', nb: 'Navn' })}</FieldLabel>
                  <FieldControl
                    value={displayName}
                    onValueChange={setDisplayName}
                    placeholder={t({ en: 'e.g. HVAC package', nb: 'f.eks. VVS-pakke' })}
                    maxLength={60}
                    disabled={renameProcurement.isPending}
                    autoFocus
                    required
                  />
                  <FieldError />
                </Field>
              </DialogScrollArea>
              <DialogFooter>
                <DialogCancel disabled={renameProcurement.isPending} />
                <Button
                  type="submit"
                  pending={renameProcurement.isPending}
                  disabled={renameProcurement.isPending}
                >
                  {t({ en: 'Save', nb: 'Lagre' })}
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPopup>
        </Dialog>

        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogPopup>
            <AlertDialogTitle>{t({ en: 'Delete pack?', nb: 'Slette pakke?' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t({
                en: `This permanently removes ${item.displayName} and its bids.`,
                nb: `Dette fjerner ${item.displayName} og tilbudene permanent.`,
              })}
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteProcurement.isPending} />
              <Button
                variant="destructive"
                pending={deleteProcurement.isPending}
                onClick={() =>
                  deleteProcurement.mutate(undefined, {
                    onSuccess: () => setIsDeleteOpen(false),
                  })
                }
              >
                {t({ en: 'Delete', nb: 'Slett' })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </div>
    </>
  );
}

function SharedContextFileCount() {
  const { workspaceId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(workspaceId, 'tenderPackage'));
  return (
    <Badge size="xs" variant="secondary">
      <Icon icon={FolderIcon} className="size-4" />
      {t({
        en: documents.length === 1 ? `${documents.length} file` : `${documents.length} files`,
        nb: documents.length === 1 ? `${documents.length} fil` : `${documents.length} filer`,
      })}
    </Badge>
  );
}

function RfpFilesHeading() {
  const { workspaceId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(workspaceId, 'tenderPackage'));
  return (
    <p className="font-medium text-sm">
      {t({ en: `RFP files (${documents.length})`, nb: `RFP-filer (${documents.length})` })}
    </p>
  );
}

function RfpFilesLink() {
  const { projectId, workspaceId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(workspaceId, 'tenderPackage'));
  if (documents.length <= 3) return null;

  return (
    <Button
      variant="secondary"
      size="xs"
      className="rounded-full mt-2"
      render={
        <Link
          to="/project/$projectId/workspaces/$workspaceId/files"
          params={{ projectId, workspaceId }}
        />
      }
    >
      {t({ en: 'Go to RFP files', nb: 'Gå til RFP-filer' })}
      <Icon icon={LinkSquare02Icon} className="size-3.5" />
    </Button>
  );
}

function NewPackButton() {
  const { workspaceId } = Route.useParams();
  const setCreateProcurementOpen = useSetAtom(createProcurementOpenAtom);
  const { data: kgStatus, isLoading: isKgStatusLoading } = useKgStatus(
    workspaceId,
    'tenderPackage',
  );
  const { data: scopeStatus } = useQuery(scopeAgentStatusQuery(workspaceId, 'tenderPackage'));
  const kgBlocked =
    isKgStatusLoading || (kgStatus != null && !canCreateWorkPackageAgainstKg(kgStatus));

  return (
    <Tooltip>
      <TooltipTrigger
        delay={0}
        render={
          <Button
            kind="with-icon"
            disabled={kgBlocked}
            onClick={() => setCreateProcurementOpen(true)}
          >
            <Icon icon={PlusSignIcon} />
            {t({ en: 'New procurement pack', nb: 'Ny anskaffelsespakke' })}
          </Button>
        }
      />
      {kgBlocked && (
        <TooltipPopup>{t(packCreationBlocker(kgStatus?.reason, scopeStatus))}</TooltipPopup>
      )}
    </Tooltip>
  );
}
